import { CommonModule, CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTypographyComponent } from 'ng-zorro-antd/typography';
import { TranslateModule } from '@ngx-translate/core';
import { AppointmentItem } from 'src/app/models/appointment-item.model';
import { Appointment } from 'src/app/models/appointment.model';
import { Location } from 'src/app/models/location.model';
import { CustomCurrencyPipe } from 'src/app/pipes/custom-currency.pipe';
import { CustomTimePipe } from 'src/app/pipes/custom-time.pipe';
import { AppointmentService } from '@services/appointment.service';
import { ExpertsService } from '@services/experts.service';

@Component({
  selector: 'app-appointment-summary',
  templateUrl: './appointment-summary.component.html',
  styleUrl: './appointment-summary.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CustomCurrencyPipe,
    CustomTimePipe,
    NzIconModule,
    NzTypographyComponent,
    NzDividerModule,
    TranslateModule,
  ],
  providers: [CurrencyPipe],
})
export class AppointmentSummaryComponent {
  @Input() appointment: Appointment | null = null;
  @Input() location?: Location;
  @Input() clickableItems = true;
  @Input() showDeleteActions = true;
  @Input() showPrices = true;
  /** Shows the amount paid and the remaining balance instead of the total. */
  @Input() showRemainingAmount = false;
  @Output() itemSelected = new EventEmitter<number>();
  @Output() itemDeleteRequested = new EventEmitter<AppointmentItem>();

  constructor(
    private appointmentService: AppointmentService,
    private expertsService: ExpertsService,
  ) {}

  get standaloneItems(): AppointmentItem[] {
    return this.appointment ? this.appointmentService.getStandaloneItems(this.appointment) : [];
  }

  get remainingAmount(): number {
    return this.appointmentService.getRemainingAmount(this.appointment);
  }

  get canInteractWithItems(): boolean {
    return this.clickableItems && this.appointment?.utmSource !== 'in-salon';
  }

  getExpertDetails(id?: number) {
    return id ? this.expertsService.getExpertDetails(id) : { displayName: '', firstName: '' };
  }

  getAddonsForItem(item: AppointmentItem): AppointmentItem[] {
    if (!this.appointment || item.id == null) {
      return [];
    }

    return this.appointmentService.getAddonsForItem(this.appointment, item.id);
  }

  getTotalPriceForItem(item: AppointmentItem): number {
    return this.appointment ? this.appointmentService.getTotalPriceForItem(this.appointment, item) : 0;
  }

  getTotalDurationForItem(item: AppointmentItem): number {
    return this.appointment ? this.appointmentService.getTotalDurationForItem(this.appointment, item) : 0;
  }

  onSelectItem(serviceItemId: number) {
    if (!this.canInteractWithItems) {
      return;
    }

    this.itemSelected.emit(serviceItemId);
  }

  onDeleteItem(item: AppointmentItem, event: Event) {
    event.stopPropagation();

    if (!this.canInteractWithItems || !this.showDeleteActions) {
      return;
    }

    this.itemDeleteRequested.emit(item);
  }
}
